import {useContext, useEffect} from 'react';

import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';

import {Box, Divider, Typography} from '@mui/material';

import {ErrorMessage} from '../../../components/ErrorMessage/ErrorMessage';
import {LoadingContainer} from '../../../components/Loading/LoadingContainer';
import {LanguageContext} from '../../../contexts/LanguageContext/LanguageContext';
import {UserContext} from '../../../contexts/UserContext/UserContext';
import {TalentPoolCountDisplay} from '../../../features/company/recruiting/talent-pool/TalentPoolCountDisplay';
import {TalentPoolFilters} from '../../../features/company/recruiting/talent-pool/TalentPoolFilters';
import {TalentPoolTable} from '../../../features/company/recruiting/talent-pool/TalentPoolTable';
import {Walkthrough, WalkthroughTrigger} from '../../../features/walkthroughs';
import {companyTalentPoolWalkthrough} from '../../../features/walkthroughs/steps/talentPool';
import {useTalentPoolSorting} from '../../../hooks/useTalentPoolSorting';
import {useTalentPoolSummary} from '../../../hooks/useTalentPoolSummary';
import {greenColors, orangeColors} from '../../../layouts/theme/designSystem';
import {CompanyAdminRoleData} from '../../../types/User';

export const TalentPoolPage = () => {
  const {currentUser} = useContext(UserContext);
  const companyId = (currentUser?.role?.data as CompanyAdminRoleData)?.companyId;
  const [searchParams, setSearchParams] = useSearchParams();
  const lang = useContext(LanguageContext);
  const {t} = useTranslation('translation', {keyPrefix: 'talentPoolPage'});

  const {
    talentPool,
    pagination,
    hasNext,
    getNext,
    loadingInitialData,
    error,
    filterBy,
    jobOptions,
    statusOptions,
    initialJobs,
    initialStatuses,
    hasListFilters,
    tablePage,
    setTablePage,
  } = useTalentPoolSorting(companyId);

  const {data: summaryPayload, isLoading: summaryLoading} = useTalentPoolSummary(companyId);

  useEffect(() => {
    const urlLang = searchParams.get('lang');

    if (urlLang !== lang) {
      setSearchParams(
        (prev) => {
          prev.set('lang', lang);
          return prev;
        },
        {replace: true}
      );
    }
  }, [lang, searchParams, setSearchParams]);

  if (error) {
    return <ErrorMessage />;
  }

  return (
    <Box sx={{maxWidth: 1120, mx: 'auto', width: '100%'}}>
      <Typography
        sx={{
          fontSize: '14px',
          lineHeight: 1.5,
          color: greenColors[700],
          mb: 0.5,
        }}
      >
        {t('breadcrumb')}
      </Typography>
      <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 3}}>
        <Typography
          component='h1'
          data-tour='talentPool.title'
          sx={{
            fontSize: '34px',
            lineHeight: 1.5,
            fontWeight: 700,
            color: greenColors[700],
          }}
        >
          {t('title')}
        </Typography>
        <WalkthroughTrigger walkthroughId='companyTalentPool' />
      </Box>

      <Box data-tour='talentPool.stats'>
        <TalentPoolCountDisplay summary={summaryPayload?.talentPoolSummary} loading={summaryLoading} />
      </Box>

      <Box
        sx={{
          bgcolor: 'white',
          border: `1px solid ${orangeColors[3]}`,
          borderRadius: '16px',
          px: {xs: 2, sm: 3},
          pt: 3,
          pb: 4,
          overflow: 'hidden',
        }}
      >
        {loadingInitialData || talentPool === null ? (
          <LoadingContainer minHeight='50vh' sx={{minHeight: {xs: '30vh', sm: '40vh', md: '50vh'}}} />
        ) : (
          <>
            <Box data-tour='talentPool.filters'>
              <TalentPoolFilters
                filterBy={filterBy}
                jobOptions={jobOptions}
                statusOptions={statusOptions}
                initialJobs={initialJobs}
                initialStatuses={initialStatuses}
              />
            </Box>
            <Divider sx={{my: 2.5, borderColor: orangeColors[3]}} />
            <TalentPoolTable
              page={tablePage}
              onPageChange={setTablePage}
              rows={talentPool}
              isFiltered={hasListFilters}
              pagination={pagination}
              hasNext={hasNext}
              getNext={getNext}
            />
          </>
        )}
      </Box>
      <Walkthrough definition={companyTalentPoolWalkthrough} enabled={!loadingInitialData && talentPool !== null} />
    </Box>
  );
};
